import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type Repository = components['schemas']['Repository']
type RepositoryScope = components['schemas']['RepositoryScope']

/**
 * `v-setrepos` — process/09-SCREEN-SPECS/v-setrepos.md. **`workspaceReplaceScope` fires from the
 * per-repository toggle directly** — the spec's own "Fired without a control" entry — never from a
 * labelled button, so the identifier is on the switch. `Export configuration` has no operation
 * (`D89`). `workspaceListRepositories` reads the relational cache, never the platform — the same
 * §7a fence every restore picker in this app already honours.
 *
 * **The spec's own Actions table names the refresh control "Reset filters" against
 * `repositories.refresh`, but the operation it fires (`workspaceRefreshRepositories`) resyncs the
 * repository list from Bitbucket — it does not clear the search box (W157).** Kept the honest
 * label ("Refresh list") rather than the spec's misleading one, the same precedent `v-alerts`'s own
 * "Reopen"/resolve mismatch already set (W150). Rendered as one of the three `icon-button`s the
 * spec's own Components table calls for in `grid-tools`; a second, real "Clear filters" clears the
 * search box (real state already on this screen); the third — a column chooser — is not built,
 * since no column-visibility state exists anywhere in the contract to back one.
 *
 * **`panel`/`grid-filter-row` added W166** — the search input, previously inline in `grid-tools`,
 * now sits in its own `grid-filter-row`; the whole table wrapped in a real `panel`. **This spec's
 * own `link` ("Edit", in `grid-tools`) is not built**: the prototype's own instance links to the
 * workspace's `auto_protect_new_repositories` setting, which lives on `Workspace`
 * (`workspaceGet`) — an operation not in this screen's own Operations table. Calling it would be
 * authorizing a new read this spec never lists, not wrapping existing content.
 */
export function VSetrepos() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const pager = useCursorPager()
  const [query, setQuery] = useState('')
  const [projectFilter, setProjectFilter] = useState('')
  const [inScopeFilter, setInScopeFilter] = useState<'' | 'true' | 'false'>('')

  const scope = useQuery({
    queryKey: ['workspace-scope', orgId, workspaceId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<RepositoryScope> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/scope', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as RepositoryScope
    },
  })

  const repositories = useQuery({
    queryKey: ['repositories', orgId, workspaceId, pager.cursor, query, inScopeFilter],
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/repositories', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { cursor: pager.cursor, q: query || undefined, in_scope: inScopeFilter ? inScopeFilter === 'true' : undefined } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const refresh = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/repositories/refresh', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      return unwrap(response as { data?: { accepted: boolean }; error?: Problem })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['repositories', orgId, workspaceId] }),
  })

  const replaceScope = useMutation({
    mutationFn: async (repositoryId: string) => {
      const current = scope.data?.exclusions ?? []
      const excluded = current.some((e) => e.repository_id === repositoryId)
      const next = excluded ? current.filter((e) => e.repository_id !== repositoryId) : [...current, { repository_id: repositoryId }]
      const client = getApiClient()
      const response = await client.PUT('/orgs/{org_id}/workspaces/{workspace_id}/scope', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: { exclusions: next },
      })
      return unwrap(response as { data?: RepositoryScope; error?: Problem })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-scope', orgId, workspaceId] })
      queryClient.invalidateQueries({ queryKey: ['repositories', orgId, workspaceId] })
    },
  })

  const problem = repositories.error instanceof ApiProblemError ? repositories.error.problem : undefined
  const rows = ((repositories.data?.items ?? []) as unknown as readonly Repository[]).filter(
    (r) => !projectFilter || (r.project_key ?? '').toLowerCase().includes(projectFilter.toLowerCase()),
  )

  return (
    <div data-scr={unitId('v-setrepos')}>
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
        <h1 className="m-0 text-section font-semibold flex-1">Repositories in scope</h1>
        <div data-cmp="grid-tools" className="flex gap-4 items-center">
          <IconButton
            data-act={undefined}
            icon="i-filter-off"
            label="Clear filters"
            disabled={!query && !projectFilter && !inScopeFilter}
            onClick={() => {
              pager.reset()
              setQuery('')
              setProjectFilter('')
              setInScopeFilter('')
            }}
          />
          <IconButton data-act={actionId('repositories.refresh')} icon="i-refresh" label="Refresh list" disabled={refresh.isPending} onClick={() => refresh.mutate()} />
          <Button data-act={undefined} ghost disabled title="No export exists for this list (D89)">
            Export configuration
          </Button>
        </div>
      </div>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2">
        <div data-cmp="grid-filter-row" className="p-4 border-b border-bd flex gap-3">
          <input
            value={query}
            onChange={(e) => {
              pager.reset()
              setQuery(e.target.value)
            }}
            placeholder="Filter by repository"
            className="border border-bd2 rounded-1 py-2 px-3 text-small"
          />
          <input
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            placeholder="Filter by project"
            className="border border-bd2 rounded-1 py-2 px-3 text-small"
          />
          <select
            value={inScopeFilter}
            onChange={(e) => {
              pager.reset()
              setInScopeFilter(e.target.value as '' | 'true' | 'false')
            }}
            className="border border-bd2 rounded-1 py-2 px-3 text-small"
          >
            <option value="">Filter by inclusion/exclusion</option>
            <option value="true">Included</option>
            <option value="false">Excluded</option>
          </select>
        </div>
        <GridTable
          columns={[
            {
              key: 'uuid',
              header: 'Bitbucket repository UUID',
              render: (r) => (
                <span className="font-mono text-small text-muted">
                  {r.repository_id.slice(0, 8)}…{r.repository_id.slice(-4)}
                </span>
              ),
            },
            { key: 'repository', header: 'Repository', render: (r) => r.name },
            { key: 'project', header: 'Project', render: (r) => <span className="text-muted">{r.project_key ?? '—'}</span> },
            {
              key: 'protected',
              header: 'Protected',
              render: (r) => (
                <label className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    data-cmp="switch"
                    data-act={actionId('scope.replace')}
                    checked={r.in_scope}
                    disabled={replaceScope.isPending}
                    onChange={() => replaceScope.mutate(r.repository_id)}
                  />
                  {r.in_scope ? 'Protected' : (r.exclusion_reason ?? 'Excluded')}
                </label>
              ),
            },
          ]}
          rows={rows}
          getRowKey={(r) => r.repository_id}
          emptyMessage={repositories.isLoading ? '' : 'No repositories match these filters.'}
        />

        <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
          <Note>We key on Bitbucket's repository UUID, which survives a rename. An excluded repository is not captured and is reported as excluded, never as failed.</Note>
          <Pager
            hasPrev={pager.hasPrev}
            hasNext={Boolean(repositories.data?.next_cursor)}
            loading={repositories.isFetching}
            onPrev={pager.goPrev}
            onNext={() => {
              if (repositories.data?.next_cursor) pager.goNext(repositories.data.next_cursor)
            }}
          />
        </div>
      </div>
    </div>
  )
}
